import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { catalogAPI } from '@/lib/api';
import { markProductsDirty } from '@/lib/offlineSync';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Search, Pencil, Check, X, RefreshCw, Trash2,
  ChevronUp, ChevronDown, ChevronsUpDown, GitMerge, PackageX,
} from 'lucide-react';

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30 inline ml-1" />;
  return sortDir === 'asc'
    ? <ChevronUp   className="w-3 h-3 inline ml-1" />
    : <ChevronDown className="w-3 h-3 inline ml-1" />;
};
const GST_OPTIONS = [0, 5, 12, 18, 28];
const PAGE_SIZE = 200;

// ── Product / Catalog Master table ────────────────────────────────────────────
export const StockMasterContent = ({ onNeedRefresh, newFromPurchaseCount: _nfp, initialCatalogId = null, autoFocusSellPrice = false, currentMrp = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [products,        setProducts]        = useState([]);
  const [total,           setTotal]           = useState(0);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [search,          setSearch]          = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [currentPage,     setCurrentPage]     = useState(1);
  const [refreshKey,      setRefreshKey]      = useState(0);
  const [loading,         setLoading]         = useState(true);
  const [editingId,       setEditingId]       = useState(null);
  const [editData,        setEditData]        = useState({});
  const [saving,          setSaving]          = useState(false);
  const [highlightId,     setHighlightId]     = useState(null);
  const [deletingId,      setDeletingId]      = useState(null);
  const [sortBy,          setSortBy]          = useState('most_sold');
  const sellPriceRef      = useRef(null);
  const autoFocusDoneRef  = useRef(false);
  const [sortDir,         setSortDir]         = useState('desc');
  const [dupGroups,       setDupGroups]       = useState([]);
  const [dupModalOpen,    setDupModalOpen]    = useState(false);
  const [mergingId,       setMergingId]       = useState(null);
  const tableScrollRef    = useRef(null);
  const topScrollRef      = useRef(null);
  const [viewMode,        setViewMode]        = useState('all');
  const [unusedProducts,  setUnusedProducts]  = useState([]);
  const [unusedTotal,     setUnusedTotal]     = useState(0);
  const [unusedLoading,   setUnusedLoading]   = useState(false);
  const unusedFetchedRef  = useRef(false);

  const fetchPage = useCallback(async (page, query, sort) => {
    setLoading(true);
    try {
      const res = await catalogAPI.getAll({
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
        ...(query ? { search: query } : {}),
        ...(!query && sort === 'most_sold' ? { sort_by: 'most_sold' } : {}),
      });
      const { items = [], total: t = 0, incomplete_count = 0 } = res.data ?? {};
      setProducts(Array.isArray(items) ? items : []);
      setTotal(t);
      setIncompleteCount(incomplete_count);
    } catch (e) {
      console.error('Failed to load catalog', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search: commit after 350 ms, reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setCommittedSearch(search);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch whenever page, committed search, sort, or refresh key changes (skip in targeted catalog_id mode)
  useEffect(() => {
    if (autoFocusSellPrice && initialCatalogId) return;
    fetchPage(currentPage, committedSearch, sortBy);
  }, [fetchPage, currentPage, committedSearch, sortBy, refreshKey, autoFocusSellPrice, initialCatalogId]);

  // Fetch duplicate barcodes on mount and after refresh
  useEffect(() => {
    catalogAPI.getDuplicateBarcodes()
      .then(r => setDupGroups(r.data?.duplicates ?? []))
      .catch(() => {});
  }, [refreshKey]);


  // Targeted fetch: when arriving via Ctrl+Shift+M, fetch exactly one product by catalog_id
  useEffect(() => {
    if (!autoFocusSellPrice || !initialCatalogId) return;
    setLoading(true);
    catalogAPI.getOne(initialCatalogId)
      .then(r => { if (r.data) setProducts([r.data]); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [autoFocusSellPrice, initialCatalogId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onNeedRefresh) onNeedRefresh.current = () => setRefreshKey(k => k + 1);
  }, [onNeedRefresh]);

  // Refresh catalog when user switches back to this tab
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setRefreshKey(k => k + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Realtime: refresh when any catalog change is broadcast (e.g. Alt+M MRP save from POS)
  useEffect(() => {
    const storeId = user?.store_id;
    if (!storeId) return;
    const channel = supabase.channel(`catalog-${storeId}`)
      .on('broadcast', { event: 'catalog_changed' }, () => {
        setRefreshKey(k => k + 1);
      })
      .on('broadcast', { event: 'catalog_created' }, () => {
        setRefreshKey(k => k + 1);
      })
      .on('broadcast', { event: 'catalog_deleted' }, () => {
        setRefreshKey(k => k + 1);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.store_id]);

  useEffect(() => {
    const hid = searchParams.get('highlight');
    if (hid) {
      setHighlightId(hid);
      setTimeout(() => {
        const el = document.getElementById(`catalog-row-${hid}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
      setTimeout(() => setHighlightId(null), 3500);
    }
  }, [searchParams]);

  // Auto-open edit for the product matching initialCatalogId when coming from POS shortcut
  useEffect(() => {
    if (!autoFocusSellPrice || autoFocusDoneRef.current || loading) return;
    if (products.length === 0) return;
    autoFocusDoneRef.current = true;  // set first — prevents infinite retry on no-match
    const match = products.find(p => p.catalog_id === initialCatalogId);
    if (match) handleEditStart(match);
  }, [products, loading, autoFocusSellPrice, initialCatalogId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When arriving via Ctrl+Shift+M, the Name field's autoFocus handles initial focus naturally.

  const isIncomplete = (p) =>
    !p.brand || !p.category || !p.hsn_code || !p.barcode;
  const isNewFromPurchase = (p) => p.source === 'auto_purchase' && isIncomplete(p);

  const handleEditStart = (p) => {
    setEditingId(p.catalog_id);
    setEditData({
      name:          p.name          || '',
      brand:         p.brand         || '',
      category:      p.category      || '',
      gst_rate:      p.gst_rate      ?? 18,
      hsn_code:      p.hsn_code      || '',
      barcode:       p.barcode       || '',
      mrp:    (autoFocusSellPrice && currentMrp != null) ? String(currentMrp) : (p.mrp != null ? String(p.mrp) : ''),
      default_discount: p.default_discount != null ? String(p.default_discount) : '0',
      _updated_at:      p.updated_at    || null,
    });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditData({});
    if (autoFocusSellPrice) navigate('/pos', { state: { fromStockMaster: true } });
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.\n\nBlocked if the product has purchase or sale history.`)) return;
    setDeletingId(p.catalog_id);
    try {
      await catalogAPI.delete(p.catalog_id);
      setProducts(prev => prev.filter(x => x.catalog_id !== p.catalog_id));
      setTotal(t => t - 1);
      setUnusedProducts(prev => prev.filter(x => x.catalog_id !== p.catalog_id));
      setUnusedTotal(t => Math.max(0, t - 1));
      if (isIncomplete(p)) setIncompleteCount(c => Math.max(0, c - 1));
      toast.success(`${p.name} deleted`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to delete';
      toast.error(detail);
    } finally {
      setDeletingId(null);
    }
  };

  const handleMerge = async (srcId, tgtId) => {
    setMergingId(srcId);
    try {
      await catalogAPI.mergeInto(srcId, tgtId);
      toast.success('Products merged successfully');
      const updated = await catalogAPI.getDuplicateBarcodes();
      setDupGroups(updated.data?.duplicates ?? []);
      if ((updated.data?.duplicates ?? []).length === 0) setDupModalOpen(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMergingId(null);
    }
  };

  const handleEditSave = async (p) => {
    if (!editData.name?.trim()) { toast.error('Product name cannot be empty'); return; }
    setSaving(true);
    try {
      const sp = parseFloat(editData.mrp);
      const spVal = sp > 0 ? sp : null;
      await catalogAPI.update(p.catalog_id, {
        name:             editData.name.trim(),
        brand:            editData.brand.trim()    || null,
        category:         editData.category.trim() || null,
        gst_rate:         parseFloat(editData.gst_rate) || 0,
        hsn_code:         editData.hsn_code.trim() || null,
        barcode:          editData.barcode.trim()  || null,
        mrp:     spVal,
        default_discount:  parseFloat(editData.default_discount) || 0,
        client_updated_at: editData._updated_at   || undefined,
      });
      if (autoFocusSellPrice) {
        // Pass the specific update in navigation state so POS can apply it
        // directly without re-fetching the full catalog from the API.
        navigate('/pos', { state: { patchedProduct: { catalog_id: p.catalog_id, name: editData.name.trim(), mrp: spVal, default_discount: parseFloat(editData.default_discount) || 0 } } });
        return;
      }
      markProductsDirty();
      const updated = { ...p, ...editData, name: editData.name.trim(), mrp: spVal, default_discount: parseFloat(editData.default_discount) || 0 };
      setProducts(prev => prev.map(prod => prod.catalog_id === p.catalog_id ? updated : prod));
      // Update incomplete count if completeness status changed
      const wasMissing = isIncomplete(p);
      const nowMissing = isIncomplete(updated);
      if (wasMissing && !nowMissing) setIncompleteCount(c => Math.max(0, c - 1));
      if (!wasMissing && nowMissing) setIncompleteCount(c => c + 1);
      setEditingId(null);
      setEditData({});
      toast.success(`${editData.name} updated`);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.includes('conflict')) {
        toast.error('Someone else updated this product. The list has been refreshed — please make your change again.');
        setEditingId(null);
        setEditData({});
        setRefreshKey(k => k + 1);
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const TxtInput = ({ field, placeholder, mono = false, onEnter }) => (
    <input
      type="text"
      value={editData[field] ?? ''}
      onChange={e => setEditData(d => ({ ...d, [field]: e.target.value }))}
      placeholder={placeholder}
      onKeyDown={e => {
        if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); }
        if (e.key === 'Escape') handleEditCancel();
      }}
      className={`w-full border border-border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${mono ? 'font-mono' : ''}`}
    />
  );

  const handleSort = (col) => {
    setSortDir(d => col === sortBy ? (d === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortBy(col);
  };

  const SortTh = ({ col, label, align = 'left', className = '' }) => (
    <th
      onClick={() => handleSort(col)}
      className={`text-${align} px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap ${className}`}
    >
      {label}<SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
    </th>
  );

  // Apply barcode column filter + relevance scoring + sort within the current server-returned page
  const displayProducts = (() => {
    let base = products;
    if (committedSearch.trim() && !editingId) {
      const lower = committedSearch.trim().toLowerCase();
      // Restrict to name/barcode matches — mirrors POS search behaviour (excludes brand/category/HSN hits)
      base = base.filter(p => {
        const bc   = String(p.barcode || '').toLowerCase();
        const name = (p.name || p.product_name || '').toLowerCase();
        return bc.includes(lower) || name.includes(lower);
      });
      const score = (p) => {
        const bc   = String(p.barcode || '').toLowerCase();
        const name = (p.name || p.product_name || '').toLowerCase();
        if (bc === lower)           return 0;
        if (bc.startsWith(lower))   return 1;
        if (name.startsWith(lower)) return 2;
        if (bc.includes(lower))     return 3;
        if (name.includes(lower))   return 4;
        return 5;
      };
      base = [...base].sort((a, b) => score(a) - score(b));
      return base;
    }
    if (editingId) return base;
    if (sortBy === 'most_sold') return base; // server-side sorted
    return [...base].sort((a, b) => {
      let av = a[sortBy] ?? '';
      let bv = b[sortBy] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  })();

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const displayUnusedProducts = (() => {
    if (!committedSearch.trim()) return unusedProducts;
    const lower = committedSearch.trim().toLowerCase();
    const filtered = unusedProducts.filter(p => {
      const bc   = String(p.barcode || '').toLowerCase();
      const name = (p.name || p.product_name || '').toLowerCase();
      return bc.includes(lower) || name.includes(lower);
    });
    const score = (p) => {
      const bc   = String(p.barcode || '').toLowerCase();
      const name = (p.name || p.product_name || '').toLowerCase();
      if (bc === lower)           return 0;
      if (bc.startsWith(lower))   return 1;
      if (name.startsWith(lower)) return 2;
      if (bc.includes(lower))     return 3;
      if (name.includes(lower))   return 4;
      return 5;
    };
    return [...filtered].sort((a, b) => score(a) - score(b));
  })();

  return (
    <div className="space-y-4">

      {/* Duplicate barcode merge modal */}
      {dupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div className="flex items-center gap-2">
                <GitMerge className="w-5 h-5 text-red-600" />
                <h2 className="text-base font-semibold">Resolve Duplicate Barcodes</h2>
              </div>
              <button onClick={() => setDupModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-muted-foreground">
                For each duplicate barcode, choose which product name to keep. The other entry's
                stock and history will be merged into the one you keep, then deleted.
              </p>
              {dupGroups.map(group => (
                <div key={group.barcode} className="border border-border rounded-xl overflow-hidden">
                  <div className="bg-muted/40 px-4 py-2 text-xs font-mono font-semibold text-muted-foreground">
                    Barcode: {group.barcode}
                  </div>
                  <div className="divide-y divide-border">
                    {group.entries.map(entry => {
                      const others = group.entries.filter(e => e.catalog_id !== entry.catalog_id);
                      return (
                        <div key={entry.catalog_id} className="flex items-center justify-between gap-3 px-4 py-3">
                          <div>
                            <p className="text-sm font-medium">{entry.name}</p>
                            <p className="text-xs text-muted-foreground">Stock: {entry.stock ?? 0}</p>
                          </div>
                          <button
                            disabled={!!mergingId}
                            onClick={async () => {
                              for (const other of others) {
                                await handleMerge(other.catalog_id, entry.catalog_id);
                              }
                            }}
                            className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
                          >
                            {mergingId ? 'Merging…' : 'Keep this one'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search + Refresh + view toggle + status chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* View toggle */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden text-xs shrink-0">
          <button
            onClick={() => setViewMode('all')}
            className={`px-3 py-1.5 transition-colors ${viewMode === 'all' ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:bg-muted/60'}`}
          >
            All products
          </button>
          <button
            onClick={() => {
              setViewMode('unused');
              if (!unusedFetchedRef.current) {
                unusedFetchedRef.current = true;
                setUnusedLoading(true);
                catalogAPI.getUnused(90, 200, 0)
                  .then(r => { setUnusedProducts(r.data?.items ?? []); setUnusedTotal(r.data?.total ?? 0); })
                  .catch(() => {})
                  .finally(() => setUnusedLoading(false));
              }
            }}
            className={`px-3 py-1.5 border-l border-border transition-colors flex items-center gap-1.5 ${viewMode === 'unused' ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:bg-muted/60'}`}
          >
            <PackageX className="w-3 h-3" />
            Unused{unusedTotal > 0 ? ` (${unusedTotal})` : ''}
          </button>
        </div>

        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search product name or barcode…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          disabled={loading}
          className="p-2 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground disabled:opacity-50"
          title="Refresh catalog"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {!loading && dupGroups.length > 0 && (
          <button
            onClick={() => setDupModalOpen(true)}
            className="text-xs text-muted-foreground border border-border rounded-full px-2.5 py-0.5 hover:bg-muted/60 transition-colors"
            title="Different products share the same barcode — this causes POS mismatch when scanning."
          >
            ⚠ {dupGroups.length} duplicate barcode{dupGroups.length !== 1 ? 's' : ''}
          </button>
        )}
        {!loading && incompleteCount > 0 && (
          <span
            className="text-xs text-muted-foreground border border-border rounded-full px-2.5 py-0.5"
            title="Products missing brand, category, HSN code, or barcode. Click the edit icon to fill in."
          >
            ○ {incompleteCount} incomplete
          </span>
        )}
      </div>

      <Card className="overflow-hidden">
        {/* Top scrollbar mirror — syncs with table scroll so user never has to scroll to the bottom */}
        <div
          ref={topScrollRef}
          className="overflow-x-auto scrollbar-wide-x border-b"
          style={{ height: '16px' }}
          onScroll={() => { if (tableScrollRef.current) tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft; }}
        >
          <div style={{ minWidth: '950px', height: '1px' }} />
        </div>

        <div
          ref={tableScrollRef}
          className="overflow-x-auto scrollbar-wide-x"
          onScroll={() => { if (topScrollRef.current) topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft; }}
        >
          <table className="w-full min-w-[950px] text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground bg-slate-100/60">Barcode</th>
                <SortTh col="name"     label="Product Name" className="bg-slate-100/60" />
                <SortTh col="brand"    label="Brand" />
                <SortTh col="category" label="Category" />
                <th className="text-left px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">Unit</th>
                <th className="text-left px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Pack Size</th>
                <th className="text-left px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">HSN Code</th>
                <SortTh col="mrp"      label="MRP" align="right" className="bg-slate-100/60" />
                <th className="text-right px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Disc %</th>
                <th className="text-right px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Net Price</th>
                <SortTh col="gst_rate" label="GST" align="center" />
                {viewMode === 'unused' && (
                  <th className="text-right px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Last sold</th>
                )}
                <th className="text-center px-2.5 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground sticky right-0 z-10 bg-muted/50 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(viewMode === 'unused' ? unusedLoading : loading) ? (
                <tr><td colSpan={viewMode === 'unused' ? 12 : 11} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : (viewMode === 'unused' ? displayUnusedProducts : displayProducts).length === 0 ? (
                <tr><td colSpan={viewMode === 'unused' ? 12 : 11} className="text-center py-10 text-muted-foreground">{viewMode === 'unused' ? (committedSearch.trim() ? 'No unused products match your search.' : 'All products have recent sales — nothing to clean up.') : 'No products found'}</td></tr>
              ) : (viewMode === 'unused' ? displayUnusedProducts : displayProducts).map((p) => {
                const isEditing      = editingId === p.catalog_id;
                const isHighlight    = highlightId === p.catalog_id;
                const fromPurchase   = !isEditing && !isHighlight && isNewFromPurchase(p);
                const needsAttention = !isEditing && !isHighlight && isIncomplete(p);

                const Missing = () => (
                  <span className="text-xs text-muted-foreground/40 italic">—</span>
                );

                return (
                  <tr
                    key={p.catalog_id}
                    id={`catalog-row-${p.catalog_id}`}
                    className={`transition-all duration-300 ${
                      isHighlight
                        ? 'bg-yellow-50 ring-2 ring-inset ring-yellow-300'
                        : isEditing
                        ? 'bg-muted/40'
                        : 'hover:bg-muted/30'
                    }`}
                  >
                    {/* Barcode — editable */}
                    <td className="px-2.5 py-2 font-mono text-sm min-w-[100px] bg-slate-50">
                      {isEditing ? (
                        <TxtInput field="barcode" placeholder="Barcode" mono onEnter={() => handleEditSave(p)} />
                      ) : (
                        p.barcode
                          ? <span className="text-muted-foreground">{p.barcode}</span>
                          : needsAttention ? <Missing /> : <span className="text-muted-foreground">—</span>
                      )}
                    </td>

                    {/* Product Name + source pill */}
                    <td className="px-2.5 py-2 font-medium min-w-[150px] bg-slate-50">
                      {isEditing ? (
                        <input type="text" value={editData.name}
                          onChange={e => setEditData(d => ({ ...d, name: e.target.value }))}
                          className="w-full border border-border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          autoFocus
                          onFocus={e => e.target.select()}
                          onKeyDown={e => { if (e.key === 'Enter') handleEditSave(p); if (e.key === 'Escape') handleEditCancel(); }}
                        />
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span>{p.name}</span>
                          {p.source && p.source !== 'stock_master' && (
                            <span className="text-[10px] text-slate-400 font-normal">
                              {p.source === 'auto_sales' && 'From sales'}
                              {p.source === 'auto_purchase' && 'From purchases'}
                              {p.source === 'auto_opening_stock' && 'From opening stock'}
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Brand — editable */}
                    <td className="px-2.5 py-2 min-w-[90px]">
                      {isEditing ? <TxtInput field="brand" placeholder="Brand" onEnter={() => handleEditSave(p)} />
                        : p.brand
                          ? <span className="text-muted-foreground text-xs">{p.brand}</span>
                          : needsAttention ? <Missing /> : <span className="text-muted-foreground">—</span>}
                    </td>

                    {/* Category — editable */}
                    <td className="px-2.5 py-2 min-w-[90px]">
                      {isEditing ? <TxtInput field="category" placeholder="Category" onEnter={() => handleEditSave(p)} />
                        : p.category
                          ? <span className="text-xs text-muted-foreground">{p.category}</span>
                          : needsAttention ? <Missing /> : <span className="text-muted-foreground">—</span>}
                    </td>

                    {/* Unit — read-only (locked) */}
                    <td className="px-2.5 py-2">
                      <span className="text-muted-foreground text-sm capitalize">{p.unit || '—'}</span>
                    </td>

                    {/* Pack Size — read-only (locked) */}
                    <td className="px-2.5 py-2">
                      {p.pack_size
                        ? <span className="text-sm text-slate-700 font-medium">{p.pack_size}</span>
                        : <span className="text-sm text-slate-400 italic">—</span>}
                    </td>

                    {/* HSN Code — editable */}
                    <td className="px-2.5 py-2 font-mono text-xs min-w-[80px]">
                      {isEditing ? <TxtInput field="hsn_code" placeholder="HSN Code" mono onEnter={() => handleEditSave(p)} />
                        : p.hsn_code
                          ? <span className="text-muted-foreground">{p.hsn_code}</span>
                          : needsAttention ? <Missing /> : <span className="text-muted-foreground">—</span>}
                    </td>

                    {/* MRP — editable */}
                    <td className="px-2.5 py-2 text-right min-w-[80px] bg-slate-50">
                      {isEditing ? (
                        <input
                          ref={sellPriceRef}
                          type="number"
                          min="0"
                          step="0.01"
                          value={editData.mrp ?? ''}
                          onChange={e => setEditData(d => ({ ...d, mrp: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleEditSave(p); if (e.key === 'Escape') handleEditCancel(); }}
                          placeholder="0.00"
                          className="w-full border border-border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      ) : p.mrp != null
                        ? <span className="text-slate-700 text-sm font-medium">₹{p.mrp}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>

                    {/* Discount — editable */}
                    <td className="px-2.5 py-2 text-right min-w-[60px]">
                      {isEditing ? (
                        <div className="flex items-center gap-1 justify-end">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={editData.default_discount ?? ''}
                            onChange={e => setEditData(d => ({ ...d, default_discount: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') handleEditSave(p); if (e.key === 'Escape') handleEditCancel(); }}
                            placeholder="0"
                            className="w-16 border border-border rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-ring"
                          />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                      ) : (p.default_discount > 0)
                        ? <span className="text-slate-700 text-xs font-medium">{p.default_discount}%</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>

                    {/* Disc. Selling Price — calculated, read-only */}
                    <td className="px-2.5 py-2 text-right min-w-[80px]">
                      {(() => {
                        const sp   = isEditing ? parseFloat(editData.mrp) : p.mrp;
                        const disc = isEditing ? (parseFloat(editData.default_discount) || 0) : (p.default_discount ?? 0);
                        if (sp == null || isNaN(sp)) return <span className="text-muted-foreground">—</span>;
                        const netPrice = Math.round(sp * (1 - Math.min(disc, 100) / 100) * 100) / 100;
                        return <span className="text-slate-700 text-xs font-medium">₹{netPrice}</span>;
                      })()}
                    </td>

                    {/* GST % — editable */}
                    <td className="px-2.5 py-2 text-center">
                      {isEditing ? (
                        <select value={editData.gst_rate}
                          onChange={e => setEditData(d => ({ ...d, gst_rate: e.target.value }))}
                          className="border border-border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
                          {GST_OPTIONS.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      ) : p.gst_rate != null
                        ? <Badge variant="outline" className="text-xs">{p.gst_rate}%</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </td>

                    {/* Last sold date — unused view only */}
                    {viewMode === 'unused' && (
                      <td className="px-2.5 py-2 text-right">
                        <span className="text-xs text-muted-foreground">
                          {p.last_sold_at
                            ? new Date(p.last_sold_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                            : <span className="italic text-muted-foreground/40">Never</span>}
                        </span>
                      </td>
                    )}

                    {/* Actions */}
                    <td className="px-2.5 py-2 sticky right-0 z-[1] bg-white shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]">
                      <div className="flex items-center justify-center gap-1">
                        {isEditing ? (
                          <>
                            <button onClick={() => handleEditSave(p)} disabled={saving}
                              className="p-1.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-40 transition-colors"
                              title="Save changes">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={handleEditCancel}
                              className="p-1.5 rounded hover:bg-red-50 text-red-400 transition-colors"
                              title="Cancel">
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleEditStart(p)}
                              className="p-1.5 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
                              title="Edit catalog info">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDelete(p)}
                              disabled={deletingId === p.catalog_id}
                              className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors disabled:opacity-40"
                              title="Delete product">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination / footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-muted-foreground">
          {viewMode === 'unused' ? (
            <>
              <span className="text-xs">
                {unusedTotal > 0
                  ? committedSearch.trim()
                    ? `${displayUnusedProducts.length} of ${unusedProducts.length} shown — filtered`
                    : `${unusedProducts.length} of ${unusedTotal.toLocaleString()} shown — not sold in 90+ days`
                  : 'No unused products'}
              </span>
              {!committedSearch.trim() && unusedProducts.length < unusedTotal && (
                <button
                  onClick={() => {
                    catalogAPI.getUnused(90, 200, unusedProducts.length)
                      .then(r => setUnusedProducts(prev => [...prev, ...(r.data?.items ?? [])]))
                      .catch(() => {});
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Load more ({unusedTotal - unusedProducts.length} remaining)
                </button>
              )}
            </>
          ) : (
            <>
              <span>
                {search.trim()
                  ? `${total.toLocaleString()} match${total !== 1 ? 'es' : ''}`
                  : `${total.toLocaleString()} products total`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1 || loading}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-muted/60 transition-colors"
                >
                  ← Prev
                </button>
                <span className="tabular-nums">
                  {currentPage} / {totalPages}
                </span>
                <button
                  disabled={currentPage >= totalPages || loading}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-muted/60 transition-colors"
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

// ── Standalone StockMaster page (backward compat for /stock-master) ──────────
const StockMaster = () => {
  const refreshRef = useRef(null);
  return (
    <div className="p-6 space-y-4">
      <StockMasterContent onNeedRefresh={refreshRef} />
    </div>
  );
};

export default StockMaster;
